import { Controller } from "@hotwired/stimulus"
import { useZoomListener } from "./mixins/use_zoom_listener.js"
import { useStableLayout } from "./mixins/use_stable_layout"

/**
 * TableStickyHeaderController mimics css position: sticky for table header.
 * Unfortunately, we can't use position: sticky because in Chrome this position type
 * creates unwanted artifacts - vertical white lines.
 *
 * So we have to invoke the necessary behavior using JS.
 *
 * The basic strategy is to create a clone of the real table header.
 * The position of the clone is fixed.
 *
 * There are the following stages during vertical table scrolling:
 * - the first row is in the user's viewport - the standard header is visible,
 *   the clone is hidden
 * - the first row is outside the user's viewport -
 *   the content of the standard header is invisible, the clone is visible
 * - only the clone and the last line remain in the user's viewport -
 *   the last line position detection is triggered
 *   and the clone scrolls smoothly with the last line
 * - the entire table is out of the user's viewport - the clone position is reset
 *
 * Application:
 * - enclose table in a div element:
 *    %div{data: { controller: 'table-sticky-header', table_sticky_header_invisible_class: 'invisible' }}
 *
 * So, for example:
 *  %div{data: { controller: 'table-sticky-header', table_sticky_header_invisible_class: 'invisible' }}
 *      %table.force-overflow-hidden{data: { table_sticky_header_target: 'table' }}
 *        %thead
 *          %tr
 *            %th
 *            %th
 *            %th...
 *        %tbody= rows
 *
 * Cooperation with HorizontalScrollController
 *
 * It is very likely that there will be a use of HorizontalScrollController around the table.
 *
 * It emits a special event 'horizontal-scroll', which we read here in TableStickyHeaderController
 * and use it to set the left position of the clone.
 *
 * It is VERY IMPORTANT if you use HorizontalScrollController and TableStickyHeaderController
 * for one table: these controllers MUST NOT be used in one div element, but must be in separate elements.
 *
 * HorizontalScrollController must be used first,
 * TableStickyHeaderController must be used in a child element this way:
 *
 * %div{data: { controller: 'horizontal-scroll' }}
 *   %div{data: { controller: 'table-sticky-header' }}
 *
 * Note that in the _adjustRightStickyCellPosition method,
 * it is necessary to move the table cell with the sticky class
 * to the right so that it has the same position in the clone
 * as in the original.
 * To do this, we need the right padding of the wrapper,
 * which is the <main> element, and move the table cell
 * with the sticky class by this value to the right.
 *
 * Used technologies: IntersectionObserver, MutationObserver
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
 */

export default class extends Controller {
  static targets = [
    "firstRowDetector",
    "lastRowDetector",
    "table",
    "tableHead",
    "tableBody",
    "tableClone",
    "lastRow",
    "tableHeaderClone",
    "rightStickyCell",
    "tableHeaderCells",
    "tableHeaderCloneCells",
  ]

  invisibleClass = ["invisible", "opacity-0"]

  /* Check if the controller is inside a dialog. */
  dialogContainer = null

  tableStructureObserver = null
  positionObserver = null
  horizontalScrollLeft = 0

  tableChangeEvent = new CustomEvent("table-sticky-header:change")

  connect() {
    if (this.element.dataset.controller.includes("horizontal-scroll")) {
      console.error("TableStickyHeaderController must not be used in the same element as HorizontalScrollController")
    }

    this._initAll()

    const horizontalScrollContainer = this.element.closest('[data-controller="horizontal-scroll"]')
    if (horizontalScrollContainer) {
      this.horizontalScrollListener = this._listenToHorizontalScroll.bind(this)
      horizontalScrollContainer.addEventListener("horizontal-scroll", this.horizontalScrollListener)
    }

    useStableLayout(this)
    useZoomListener(this)
  }

  disconnect() {
    window.dispatchEvent(this.tableChangeEvent)

    window.removeEventListener("horizontal-scroll", this.horizontalScrollListener)

    this._disconnectObservers()
  }

  /* Disconnect all observers associated with this controller to avoid
   * memory leaks and duplicate callbacks. */
  _disconnectObservers() {
    this.positionObserver?.disconnect()
    this.tableStructureObserver?.disconnect()
  }

  _initAll() {
    this._setupLayout()
    this._setupObservers()
    this._setupEventListeners()
  }

  /* Prepare DOM structure and sizing needed for sticky header behaviour:
   * targets, detectors, table/header clone, last-row detector positioning. */
  _setupLayout() {
    this._markTargets()
    this._createFirstRowDetector()
    this._createLastRowDetector()

    this._maybeCloneTable()
    this._layoutLastRowDetector()

    this._checkForParentWithHorizontalScroll()
  }

  /* Create and connect IntersectionObserver(s) for first/last row detectors. */
  _setupObservers() {
    this._initObserver()
    this._connectObserver()
  }

  /* Observe table structure changes, so layout and the header clone stay in sync. */
  _setupEventListeners() {
    this._listenToTableStructureChange()
  }

  _reinit() {
    const headerIsSticky = this._headerIsSticky()

    window.dispatchEvent(this.tableChangeEvent)

    this._disconnectObservers()
    this._setupLayout()
    this._setupObservers()
    this._setupEventListeners()

    if (headerIsSticky) {
      this._stickHeader()
    }
  }

  _listenToTableStructureChange = () => {
    this.tableStructureObserver = new MutationObserver((mutations) => {
      const rowsChanged = mutations.some((mutation) => mutation.target === this.tableBodyTarget)
      const headerChanged = mutations.some(
        (mutation) => mutation.target === this.tableHeadTarget || mutation.target.tagName === "TR",
      )

      if (rowsChanged) {
        this._reinit()
      }
      if (headerChanged) {
        this._rebuildTableHeaderClone()
      }
    })

    /* Header cells are nested in rows, while body updates only require direct row changes. */
    if (this.hasTableHeadTarget) {
      this.tableStructureObserver.observe(this.tableHeadTarget, { childList: true, subtree: true })
    }
    if (this.hasTableBodyTarget) {
      this.tableStructureObserver.observe(this.tableBodyTarget, { childList: true })
    }
  }

  _rebuildTableHeaderClone() {
    const headerIsSticky = this._headerIsSticky()

    this.tableHeaderCloneTarget.replaceChildren()
    this._maybeCloneTable()
    this._layoutLastRowDetector()

    if (headerIsSticky) {
      this._stickHeader()
    }

    this.element.dispatchEvent(new CustomEvent("table-sticky-header:content-width-change", { bubbles: true }))
  }

  _headerIsSticky() {
    return this.tableHeadTarget.classList.contains(this.invisibleClass[0])
  }

  _markTargets() {
    if (!this.hasTableTarget) {
      this.element.querySelector("table").setAttribute("data-table-sticky-header-target", "table")
    }

    if (!this.hasTableHeadTarget) {
      this.tableTarget.querySelector("thead").setAttribute("data-table-sticky-header-target", "tableHead")
    }

    if (!this.hasTableBodyTarget) {
      this.tableTarget.querySelector("tbody").setAttribute("data-table-sticky-header-target", "tableBody")
    }

    if (!this.hasTableHeaderCellsTarget) {
      this.tableHeadTarget.querySelectorAll("th").forEach((th) => {
        th.setAttribute("data-table-sticky-header-target", "tableHeaderCells")
      })
    }

    this.dialogContainer = this.element.closest("dialog")
  }

  _createFirstRowDetector() {
    if (this.hasFirstRowDetectorTarget || !this.hasTableTarget) return

    const firstRowDetectorElement = document.createElement("div")
    firstRowDetectorElement.setAttribute("data-table-sticky-header-target", "firstRowDetector")
    firstRowDetectorElement.classList.add("relative")
    this.element.insertBefore(firstRowDetectorElement, this.tableTarget)
    this.firstRowDetectorTarget.style.width = `${this.tableTarget.offsetWidth}px`
  }

  _createLastRowDetector() {
    if (this.hasLastRowDetectorTarget || !this.hasTableTarget) return

    const lastRowDetectorElement = document.createElement("div")
    lastRowDetectorElement.setAttribute("data-table-sticky-header-target", "lastRowDetector")
    lastRowDetectorElement.classList.add("relative", "w-full")

    const lastRow = this.tableBodyTarget.querySelector("tr:last-child")
    if (lastRow) {
      lastRow.setAttribute("data-table-sticky-header-target", "lastRow")
    }
    this.element.appendChild(lastRowDetectorElement)
  }

  /* Listen to horizontal scroll from HorizontalScrollController
   * and set the left position of the cloned header.
   * To select the correct elements, we need to read the container from the event
   * and work with the elements in that container. */
  _listenToHorizontalScroll(e) {
    this.horizontalScrollLeft = e.detail.scrollLeft
    this.tableHeaderCloneTarget.style.left = `${this.tableHeaderCloneLeft - this.horizontalScrollLeft}px`
    this.firstRowDetectorTarget.style.left = `${this.horizontalScrollLeft}px`
    this.lastRowDetectorTarget.style.left = `${this.horizontalScrollLeft}px`
  }

  _initObserver = () => {
    const stickyTop = this._stickyTop()

    this.positionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (this._isFirstRow(entry)) {
            this._firstRowEntryChanged(entry)
          }
          if (this._isLastRow(entry)) {
            this._handleLastRowIntersection(entry)
          }
        })
      },
      { rootMargin: `${stickyTop * -1}px 0px 0px 0px` },
    )
    this._connectObserver()
  }

  _connectObserver() {
    this.positionObserver.observe(this.firstRowDetectorTarget)
    this.positionObserver.observe(this.lastRowDetectorTarget)
  }

  _firstRowEntryChanged(entry) {
    const observerTop = entry.rootBounds?.top ?? this._stickyTop()
    const isAboveStickyTop = !entry.isIntersecting && entry.boundingClientRect.bottom < observerTop

    if (isAboveStickyTop) {
      this._stickHeader()
    } else {
      this._unstickHeader()
    }
  }

  /* In a situation where there is only the tableHeader clone
   * and the last row left to scroll, we need to synchronize
   * the top position of the clone with the last row. */
  _handleLastRowIntersection(entry) {
    const { isIntersecting } = entry

    const scrollDirection = this._detectScrollDirection(entry)

    if (isIntersecting && scrollDirection === "up") {
      window.removeEventListener("scroll", this._listenToVerticalScroll)
    }

    if (isIntersecting && scrollDirection === "down") {
      this._stickHeader()
    }

    if (!isIntersecting && scrollDirection === "down") {
      window.addEventListener("scroll", this._listenToVerticalScroll)
    }

    this._setCloneTopToContainerTop()
  }

  /* The header is stuck to the top of the viewport
   * when the first row is not in the viewport. */
  _stickHeader() {
    this.tableHeadTarget.classList.add(...this.invisibleClass)
    this.tableHeaderCloneTarget.classList.remove(...this.invisibleClass, "relative")
  }

  /* The header is unstuck when the first row is in the viewport. */
  _unstickHeader() {
    this.tableHeaderCloneTarget.classList.add(...this.invisibleClass)
    this.tableHeadTarget.classList.remove(...this.invisibleClass)
  }

  _isFirstRow(entry) {
    return entry.target === this.firstRowDetectorTarget
  }

  _isLastRow(entry) {
    return entry.target === this.lastRowDetectorTarget
  }

  /* Find the last row of the table body, get its height
   * and set the top position of the last row detector. */
  _layoutLastRowDetector() {
    const lastRowHeight = this.hasLastRowTarget ? this.lastRowTarget.offsetHeight : 0
    const lastRowWidth = this.hasLastRowTarget ? this.lastRowTarget.offsetWidth : 0
    const tableMarginBottom = parseInt(getComputedStyle(this.tableTarget).marginBottom, 10) || 0

    const offset = (lastRowHeight + this.tableHeaderCloneTarget.offsetHeight + tableMarginBottom) * -1

    this.lastRowDetectorTarget.style.top = `${offset}px`
    this.lastRowDetectorTarget.style.width = `${lastRowWidth}px`

    /** Sync first row detector width with last row detector. */
    this.firstRowDetectorTarget.style.width = `${lastRowWidth}px`
  }

  /* Create clone of the table if it doesn't exist yet'.
   * This clone will be wrapper for sticky table header. */
  _maybeCloneTable() {
    if (!this.hasTableCloneTarget) {
      const wrappingTable = document.createElement("table")
      wrappingTable.setAttribute("data-table-sticky-header-target", "tableClone")
      this.element.appendChild(wrappingTable)
      this._copyClassList(this.tableTarget, this.tableCloneTarget)
      this.tableCloneTarget.style.border = "none"
    }
    this.tableCloneTarget.style.width = this.tableTarget.getBoundingClientRect().width + "px"

    const { colWidths, positions } = this._getGridProperties()

    if (!this.hasTableHeaderCloneTarget) {
      const gridContainer = this._createGridContainer(colWidths)
      this.tableCloneTarget.appendChild(gridContainer)
    } else {
      this._setGridTemplateColumns(this.tableHeaderCloneTarget, colWidths)
    }

    /** Create a cell for each header cell in original table
     * and distribute saved positions/sizes
     */
    this._setCloneTableHeaderCellsDimensions(positions, colWidths)

    this._copyClassList(this.tableHeadTarget, this.tableHeaderCloneTarget)
    this._adjustHeaderClone()
  }

  _setCloneTableHeaderCellsDimensions(positions, colWidths) {
    if (this.hasTableHeaderCloneCellsTarget) {
      this.tableHeaderCloneCellsTargets.forEach((th, index) => {
        const position = positions[index]
        const cellWidth = colWidths
          .slice(position.col - 1, position.col - 1 + position.colspan)
          .reduce((sum, w) => sum + w, 0)
        th.style.width = `${cellWidth}px`
      })
    } else {
      positions.forEach((position) => {
        const cell = document.createElement("th")

        const computed = this._getDefaultStyles(position.th)
        for (let i = 0; i < computed.length; i++) {
          const prop = computed[i]
          cell.style[prop] = computed.getPropertyValue(prop)
        }

        position.th.childNodes.forEach((child) => {
          cell.appendChild(child.cloneNode(true))
        })

        this._copyClassList(position.th, cell)
        this._copyDataAttributes(position.th, cell)

        const cellWidth = colWidths
          .slice(position.col - 1, position.col - 1 + position.colspan)
          .reduce((sum, w) => sum + w, 0)
        cell.style.width = `${cellWidth}px`
        cell.style.display = "block"

        cell.style.gridColumn = `${position.col} / span ${position.colspan}`
        cell.style.gridRow = `${position.row} / span ${position.rowspan}`

        cell.setAttribute("data-table-sticky-header-target", "tableHeaderCloneCells")

        this.tableHeaderCloneTarget.appendChild(cell)
      })
    }
  }

  /** Get computed styles of an element
   * that differ from default <th> styles.
   */
  _getDefaultStyles(element) {
    const defaultTh = document.createElement("th")
    defaultTh.style.display = "none"
    document.body.appendChild(defaultTh)

    const defaultStyles = getComputedStyle(defaultTh)
    const computed = getComputedStyle(element)

    const customStyles = {}
    for (let i = 0; i < computed.length; i++) {
      const prop = computed[i]
      if (computed.getPropertyValue(prop) !== defaultStyles.getPropertyValue(prop)) {
        customStyles[prop] = computed.getPropertyValue(prop)
      }
    }

    document.body.removeChild(defaultTh)

    return customStyles
  }

  /** Copy complete class list
   * from source element to target element
   */
  _copyClassList(source = null, target = null) {
    if (!source || !target) {
      console.error("TableStickyHeaderController: source or target is null in _copyClassList")
      return
    }
    source.classList.forEach((cls) => target.classList.add(cls))
  }

  /** Copy all data-* attributes
   *  from source element to target element
   */
  _copyDataAttributes(source = null, target = null) {
    if (!source || !target) {
      console.error("TableStickyHeaderController: source or target is null in _copyDataAttributes")
      return
    }

    Array.from(source.attributes)
      .filter((attr) => attr.name.startsWith("data-"))
      .forEach((attr) => {
        target.setAttribute(attr.name, attr.value)
      })
  }

  _createGridContainer(colWidths) {
    /** Create a <tr> element to act as grid container */
    const gridContainer = document.createElement("tr")
    gridContainer.setAttribute("data-table-sticky-header-target", "tableHeaderClone")

    /** Use CSS grid for layout */
    gridContainer.style.display = "grid"

    /** Set grid container to be fixed positioned inside viewport */
    gridContainer.style.position = "fixed"

    /** Remove any gaps between grid columns */
    gridContainer.style.gap = "0"

    /** Set grid columns to match widths of original header cells */
    this._setGridTemplateColumns(gridContainer, colWidths)

    gridContainer.style.alignItems = "center"

    /** Ensure borders are collapsed (for table look) */
    gridContainer.style.borderCollapse = "collapse"

    /** Use border-box sizing for consistent width calculations */
    gridContainer.style.boxSizing = "border-box"

    /** Set grid container width to match original table */
    gridContainer.style.width = `${this.tableTarget.getBoundingClientRect().width}px`

    return gridContainer
  }

  _setGridTemplateColumns(gridContainer, colWidths) {
    gridContainer.style.gridTemplateColumns = colWidths.map((width) => `${width}px`).join(" ")
  }

  /** Calculate columns widths and positions */
  _getGridProperties() {
    const rows = Array.from(this.tableHeadTarget.querySelectorAll("tr"))
    /**  Array to store calculated column widths */
    const colWidths = []
    /** Track which grid cells are occupied (for colspan/rowspan) */
    const colOccupied = []
    /** Store position info for each header cell */
    const positions = []

    rows.forEach((tr, rowIndex) => {
      let colIndex = 0
      Array.from(tr.children).forEach((th) => {
        /** Find first free column in this row */
        while (colOccupied[rowIndex]?.[colIndex]) colIndex++
        const colspan = parseInt(th.getAttribute("colspan")) || 1
        const rowspan = parseInt(th.getAttribute("rowspan")) || 1
        const width = th.getBoundingClientRect().width

        /** Divide cell width by colspan to get width per column */
        const partWidth = width / colspan

        /** Save grid position and cell reference */
        positions.push({
          row: rowIndex + 1,
          col: colIndex + 1,
          rowspan,
          colspan,
          th,
        })

        /** Assign calculated width to each spanned column */
        for (let i = 0; i < colspan; i++) {
          colWidths[colIndex + i] = partWidth

          /** Mark grid cells as occupied for rowspan/colspan */
          for (let r = 0; r < rowspan; r++) {
            if (!colOccupied[rowIndex + r]) colOccupied[rowIndex + r] = []
            colOccupied[rowIndex + r][colIndex + i] = true
          }
        }

        /** Move to next column after colspan */
        colIndex += colspan
      })
    })
    return {
      colWidths,
      positions,
    }
  }

  /** Set clone classes and left position.  */
  _adjustHeaderClone() {
    const { left: headerLeft, height: headerHeight, width: headerWidth } = this.tableHeadTarget.getBoundingClientRect()
    this.tableHeaderCloneLeft = headerLeft + this.horizontalScrollLeft

    this._setHeaderCloneBackgroundColor()

    this.tableHeaderCloneTarget.classList.add(...this.invisibleClass)
    this.tableHeaderCloneTarget.style.left = `${this.tableHeaderCloneLeft - this.horizontalScrollLeft}px`
    this.tableHeaderCloneTarget.style.width = `${headerWidth}px`
    this.tableHeaderCloneTarget.style.height = `${headerHeight}px`
    this.tableHeaderCloneTarget.style.position = "fixed"
    this.tableHeaderCloneTarget.style.zIndex = "30"
    this._changeStickyCellsPositions()
  }

  /** Set background color of clone container
   *  to match original table header background color.
   *  We check thead, then first tr, then each th
   *  until we find a non-transparent background color.
   *  If none is found, we leave background color unset,
   *  allowing it to inherit from CSS.
   */
  _setHeaderCloneBackgroundColor() {
    let backgroundColor = null

    const customTheadBg = this._hasCustomBackgroundColor(this.tableHeadTarget)

    if (customTheadBg) {
      backgroundColor = customTheadBg
    } else {
      const customTheadRowBg = this._hasCustomBackgroundColor(this.tableHeadTarget.querySelector("tr"))
      if (customTheadRowBg) {
        backgroundColor = customTheadRowBg
      } else {
        const tableHeadRow = this.tableHeadTarget.querySelector("tr")
        const ths = Array.from(tableHeadRow.querySelectorAll("th"))
        for (const th of ths) {
          const customThBg = this._hasCustomBackgroundColor(th)
          if (customThBg) {
            backgroundColor = customThBg
            break
          }
        }
      }
    }

    if (backgroundColor) {
      this.tableHeaderCloneTarget.style.backgroundColor = backgroundColor
    }
  }

  /** Check if element has a custom
   * (non-transparent) background color
   */
  _hasCustomBackgroundColor(element = null) {
    if (!element) {
      console.error("TableStickyHeaderController: element is null in _hasCustomBackgroundColor")
      return
    }

    let backgroundColor = null
    const computed = getComputedStyle(element)
    if (
      computed.backgroundColor &&
      computed.backgroundColor !== "rgba(0, 0, 0, 0)" &&
      computed.backgroundColor !== "transparent"
    ) {
      backgroundColor = computed.backgroundColor
    }
    return backgroundColor
  }

  /** As position sticky inside grid shifts element to right,
   * we need to use position fixed and recalculate left position.
   */
  _changeStickyCellsPositions() {
    const stickyOriginalCells = this.tableHeadTarget.querySelectorAll(".sticky")
    const stickyCloneCells = this.tableHeaderCloneTarget.querySelectorAll(".sticky")

    stickyOriginalCells.forEach((stickyOriginal, i) => {
      const stickyClone = stickyCloneCells[i]
      if (stickyOriginal && stickyClone) {
        const { left } = stickyOriginal.getBoundingClientRect()
        stickyClone.classList.remove("sticky")
        stickyClone.classList.add("fixed")
        stickyClone.style.left = `${left}px`
      }
    })
  }

  /* If this element has a parent with horizontal scroll controller,
   * we need to add force-overflow-hidden class to this element
   * to show only necessary part of stuck table header. */
  _checkForParentWithHorizontalScroll() {
    const parentWithHorizontalScroll = this.element.closest('[data-controller="horizontal-scroll"]')
    if (parentWithHorizontalScroll) {
      parentWithHorizontalScroll.classList.add("force-overflow-hidden")
    }
  }

  /* Detect scroll direction - we need this
   * to set the behaviour of the clone. */
  _detectScrollDirection(entry) {
    const observerTop = entry.rootBounds?.top ?? this._stickyTop()

    if (entry.boundingClientRect.y <= observerTop) {
      return "down"
    } else {
      return "up"
    }
  }

  /* Reset the header clone's top position to the sticky container top:
   * - if the table is in a dialog, align the clone with the top of the dialog,
   * - otherwise align it below the application header, or with the top of the
   *   viewport when the layout has no application header.
   *
   * This is the default sticky position. When the last row is scrolling out
   * of view, we temporarily override this via _listenToVerticalScroll(), which
   * makes the clone follow the last row detector. */
  _setCloneTopToContainerTop() {
    this.tableHeaderCloneTarget.style.top = `${this._stickyTop()}px`
  }

  _stickyTop() {
    if (this.dialogContainer) {
      return this.dialogContainer.getBoundingClientRect().top
    }

    const applicationHeader = document.querySelector("[data-application-header]")

    return Math.max(applicationHeader?.getBoundingClientRect().bottom ?? 0, 0)
  }

  /* Sync top position as the last row moves while scrolling */
  _listenToVerticalScroll = () => {
    if (!this.hasTableHeaderCloneTarget || !this.hasLastRowDetectorTarget) return
    this.tableHeaderCloneTarget.style.top = `${this.lastRowDetectorTarget.getBoundingClientRect().top}px`
  }

  onLayoutResizeChange() {
    this._handleExternalLayoutChange()
  }

  onZoomChange() {
    this._handleExternalLayoutChange()
  }

  /* Common handler for external layout-affecting events (resize, zoom,
   * left panel changes). Rebuilds layout and observers from scratch. */
  _handleExternalLayoutChange() {
    this._reinit()
  }
}
